import React from 'react';
import { Link } from 'react-router-dom';
import { Car, MapPin, Phone, Mail, Instagram } from 'lucide-react';
import { useAPI } from '../hooks/useAPI';
import { locationsAPI } from '../services/api';
import { phoneToWhatsAppUrl, emailToMailto } from '../lib/contact';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  // Récupérer les vraies adresses depuis l'API
  const { data: locations } = useAPI(() => locationsAPI.getAll());

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Logo et description */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_detailing-portal/artifacts/4bgvvezy_Black%20and%20Blue%20Modern%20Automotive%20Carwash%20Logo.avif" 
                alt="AutoSpa 77 - Detailing Automobile"
                className="h-40 w-auto"
                onError={(e) => {
                  // Fallback si l'image ne charge pas
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="flex items-center space-x-2" style={{display: 'none'}}>
                <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-xl font-bold">AutoSpa</span>
                  <span className="text-xl font-bold text-blue-400 ml-1">77</span>
                </div>
              </div>
            </div>
            <p className="text-gray-400 text-sm">
              Centre de detailing automobile professionnel en Seine-et-Marne. 
              Nous redonnons vie à votre véhicule avec passion et expertise.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Navigation</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-400 hover:text-blue-400 transition-colors duration-200">Accueil</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-blue-400 transition-colors duration-200">Services</Link></li>
              <li><Link to="/galerie" className="text-gray-400 hover:text-blue-400 transition-colors duration-200">Galerie</Link></li>
              <li><Link to="/tarifs" className="text-gray-400 hover:text-blue-400 transition-colors duration-200">Tarifs</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-blue-400 transition-colors duration-200">Contact</Link></li>
              <li><Link to="/reservation" className="text-gray-400 hover:text-blue-400 transition-colors duration-200">Réserver</Link></li>
            </ul>
          </div>

          {/* Suivez-nous */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Suivez-nous</h3>
            <a 
              href="https://www.instagram.com/auto_spa_77/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-gray-400 hover:text-pink-400 transition-colors duration-200"
            >
              <Instagram className="w-5 h-5" />
              <span className="text-sm">@auto_spa_77</span>
            </a>
          </div>
        </div>

        {/* Nos adresses */}
        <div className="mt-12">
          <h3 className="text-lg font-semibold mb-6">Nos Centres</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations?.map((location) => (
              <div
                key={location.id}
                className="rounded-xl bg-gray-800/50 p-5 space-y-3 transition-colors duration-200 hover:bg-gray-800"
                data-testid={`footer-location-${location.id}`}
              >
                <h4 className="font-semibold text-blue-400 flex items-center gap-2">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  {location.name?.replace('AutoSpa 77 - ', '')}
                </h4>
                <p className="text-sm text-gray-300 leading-relaxed">{location.address}</p>
                <div className="flex flex-col gap-2 text-sm">
                  {location.phone && (
                    <a
                      href={phoneToWhatsAppUrl(location.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-gray-300 hover:text-green-400 transition-colors duration-200"
                      data-testid={`footer-phone-${location.id}`}
                    >
                      <Phone className="w-4 h-4" />
                      <span>{location.phone}</span>
                    </a>
                  )}
                  {location.email && (
                    <a
                      href={emailToMailto(location.email)}
                      className="inline-flex items-center gap-2 text-gray-300 hover:text-blue-400 transition-colors duration-200 break-all"
                      data-testid={`footer-email-${location.id}`}
                    >
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span>{location.email}</span>
                    </a>
                  )}
                </div>
                {location.hours && (
                  <div className="text-xs text-gray-400 border-t border-gray-700/60 pt-3 space-y-1">
                    {Object.entries(location.hours).map(([day, hours]) => (
                      <div key={day} className="flex justify-between gap-2">
                        <span className="text-gray-500">{day}</span>
                        <span>{hours}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 mt-8 pt-8 text-center">
          <p className="text-gray-400 text-sm">
            © {currentYear} AutoSpa 77. Tous droits réservés. Centre de detailing automobile professionnel.
          </p>
          <div className="mt-2">
            <Link to="/admin/login" className="text-gray-500 hover:text-gray-300 text-xs transition-colors duration-200">
              Administration
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;